/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Notify } from "notiflix/build/notiflix-notify-aio";
import { Loading } from "notiflix/build/notiflix-loading-aio";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Edit from "@mui/icons-material/Edit";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import ControlledTextInput from "components/FormInputs/ControlledTextInput";
import ControlledSelect from "components/FormInputs/ControlledSelect";
import MDButton from "components/MDButton";
import { globalCountry } from "store/entities/countries";
import {
  useLazyGetAvailableResellerCreditsQuery,
  useEditResellerCreditsMutation,
} from "services/credit_note";
import { useLazyGetResellersByCompanyIdQuery } from "services/resellers";
import { creditNotesSchema } from "../schemas/_model";
import { useLazyGetAllOrdersByCompanyQuery } from "services/orders";
import { Block } from "notiflix/build/notiflix-block-aio";
import { useHistory } from "react-router-dom";
import { useLazyGetOrderProductsByOrderIdQuery } from "services/orders";
import { TextField, Typography } from "@mui/material";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const EditCreditNoteDialog = ({ creditData, companiesList }) => {
  const initialFormState = {
    company_id: 0,
    reseller_id: 0,
    amount: 0,
    current_total: 0,
    order_id: 0,
    order_product_ids: [],
  };
  const navigate = useHistory();
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [resellers, setResellers] = useState([]);
  const [availableCredits, setAvailableCredits] = useState(0);
  const [orders, setOrders] = useState([]);
  const [orderProducts, setOrderProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const selectedCountry = useSelector(globalCountry);

  const { control, watch, handleSubmit, setValue, reset } = useForm({
    resolver: yupResolver(creditNotesSchema),
    defaultValues: initialFormState,
  });

  const selectedCompany = watch("company_id");
  const selectedReseller = watch("reseller_id");
  const orderId = watch("order_id");
  const selectedOrderProducts = watch("order_product_ids");

  const [triggerAvailableCredits] = useLazyGetAvailableResellerCreditsQuery();
  const [triggerResellers] = useLazyGetResellersByCompanyIdQuery();
  const [triggerOrders] = useLazyGetAllOrdersByCompanyQuery();
  const [triggerOrderProducts] = useLazyGetOrderProductsByOrderIdQuery();

  const [putCreditNote] = useEditResellerCreditsMutation();

  const handleClickOpen = () => {
    setOpenEditDialog(true);
  };

  const handleClose = () => {
    setOpenEditDialog(false);
  };

  const setCreditDataValues = () => {
    if (creditData && Object.keys(creditData).length > 0) {
      setValue("company_id", creditData.reseller.company_id);
      setValue("reseller_id", creditData.reseller_id);
      setValue("amount", creditData.amount);
      setValue("current_total", creditData.current_total);
      setValue("order_id", creditData.order_id);
    }
  };
  useEffect(() => {
    setCreditDataValues();
  }, [creditData]);

  // ⚡️ recalculate whenever order changes
  useEffect(() => {
    let order = orders.find((x) => x.id == orderId);
    if (order) {
      setValue("amount", order.grandtotal);
      // fetch order products
      triggerOrderProducts(orderId).then((res) => setOrderProducts(res.data));
      setSelectedProducts([]);
      setValue("order_product_ids", []);
    }
  }, [orderId, availableCredits, setValue]);

  // ⚡️ recalculate whenever order products change
  useEffect(() => {
    let order = orders.find((x) => x.id == orderId);
    let total = order ? order.grandtotal : 0;
    if (selectedOrderProducts && selectedOrderProducts.length > 0) {
      const selected = orderProducts.filter((p) => selectedOrderProducts.includes(p.id));
      setSelectedProducts(
        selected.map((p) => ({
          ...p,
          userQty: p.quantity,
        }))
      );

      // recalc total amount
      total = selected.reduce((sum, p) => sum + p.price * p.quantity, 0);
    } else {
      setSelectedProducts([]);
      setValue("amount", total);
    }
  }, [selectedOrderProducts, setValue]);

  useEffect(() => {
    if (selectedCompany && selectedCompany != 0) {
      getResellers(selectedCompany);
      getOrders(selectedCompany);
    }
  }, [creditData, selectedCompany]);

  useEffect(() => {
    if (selectedReseller) {
      getAvailableCredits(selectedReseller);
    }
  }, [selectedReseller]);

  const getResellers = async (company_id) => {
    triggerResellers(company_id).then((resellers) => {
      setResellers(resellers.data);
    });
  };

  const getOrders = async (company_id) => {
    triggerOrders(company_id).then((orders) => {
      setOrders(orders.data);
    });
  };

  const getAvailableCredits = async (reseller_id) => {
    Block.dots("#blockDesc", { svgColor: "rgba(159, 3, 3, 0.8)" });

    triggerAvailableCredits(reseller_id).then((credits) => {
      setAvailableCredits(credits.data);
    });

    Block.remove("#blockDesc", 500);
  };

  const onSubmit = (data) => {
    Loading.dots({
      svgColor: "rgba(159, 3, 3, 0.8)",
    });

    try {
      data.id = creditData.id;
      data.countryId = selectedCountry.id;

      const payload = {
        ...data,
        order_product_ids: selectedProducts.map((p) => p.id),
        products: selectedProducts.map((p) => ({
          id: p.id,
          quantity: p.userQty,
          price: p.price,
        })),
        amount: data.amount,
      };

      putCreditNote(payload).then((response) => {
        console.log(response);
        if (response.error) {
          Notify.failure(response.error.data.message);
        } else {
          Notify.success("Succefully Updated!");
        }
      });
    } catch (e) {
      console.log(e);
    } finally {
      Loading.remove(500);
      handleClose();
      navigate.push(`/user/credit-notes`);
    }
  };

  return (
    <>
      <Tooltip title="Edit Creditnote" placement="top">
        <IconButton aria-label="view" size="small" sx={{ ml: 1 }} onClick={handleClickOpen}>
          <Edit color="warning" fontSize="inherit" />
        </IconButton>
      </Tooltip>
      <BootstrapDialog
        // onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        maxWidth="xl"
        sx={{
          "& .MuiDialog-paper": {
            width: "500px",
          },
        }}
        open={openEditDialog}
      >
        <DialogTitle sx={{ m: 0, p: 2, color: "#3f3f3f" }} id="customized-dialog-title">
          Edit Credit Note
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          <Box
            sx={{
              fontWeight: 500,
              color: "#3f3f3f",
              mt: 3,
            }}
          >
            {availableCredits ? (
              <h3 id="blockDesc" className="mb-2">
                {" "}
                Available credits: {availableCredits}
              </h3>
            ) : (
              <></>
            )}
            <form>
              <GridContainer>
                <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
                  <ControlledSelect
                    name={"company_id"}
                    control={control}
                    label="Select company *"
                    menu={{
                      id: "id",
                      value: "name",
                      title: "Company",
                      list: companiesList,
                    }}
                    sx={{
                      "& .MuiFormLabel-root": {
                        backgroundColor: "#fff",
                      },
                      "& .MuiInputBase-root": {
                        height: "44px",
                      },
                    }}
                  />
                </GridItem>
                <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
                  <ControlledSelect
                    name={"reseller_id"}
                    control={control}
                    label="Select Reseller *"
                    disabled={selectedCompany == 0}
                    menu={{
                      id: "id",
                      value: "first_name",
                      title: "Reseller",
                      list: resellers,
                    }}
                    sx={{
                      "& .MuiFormLabel-root": {
                        backgroundColor: "#fff",
                      },
                      "& .MuiInputBase-root": {
                        height: "44px",
                      },
                    }}
                  />
                </GridItem>
                <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
                  <ControlledSelect
                    name={"order_id"}
                    control={control}
                    label="Select an Order *"
                    disabled={selectedReseller == 0}
                    menu={{
                      id: "id",
                      value: "invoice_number",
                      title: "Order",
                      list: orders,
                    }}
                    sx={{
                      "& .MuiFormLabel-root": {
                        backgroundColor: "#fff",
                      },
                      "& .MuiInputBase-root": {
                        height: "44px",
                      },
                    }}
                  />
                </GridItem>
                {/* 🆕 Multi-select for products */}
                {orderProducts.length > 0 && (
                  <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
                    <ControlledSelect
                      name={"order_product_ids"}
                      control={control}
                      label="Select Products (Optional)"
                      multiple
                      menu={{
                        id: "id",
                        value: "name",
                        title: "Products",
                        list: orderProducts,
                      }}
                      sx={{
                        "& .MuiFormLabel-root": { backgroundColor: "#fff" },
                        "& .MuiInputBase-root": { minHeight: "44px" },
                      }}
                    />
                  </GridItem>
                )}

                {/* 🆕 Editable quantity list for each selected product */}
                {selectedProducts && selectedProducts.length > 0 && (
                  <GridItem xs={12}>
                    <Box sx={{ border: "1px solid #ddd", borderRadius: 2, p: 2, mt: 2 }}>
                      {selectedProducts.map((prod, i) => (
                        <Box
                          key={prod.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 1.5,
                            mb: 1,
                            borderRadius: 2,
                            border: "1px solid #e0e0e0",
                            backgroundColor: "#fafafa",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                          }}
                        >
                          <Box>
                            <Typography variant="subtitle1">{prod.name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Ordered: {prod.quantity}
                            </Typography>
                          </Box>

                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography variant="body2" sx={{ minWidth: 65 }}>
                              Quantity
                            </Typography>
                            <TextField
                              type="number"
                              size="small"
                              variant="outlined"
                              inputProps={{
                                min: 1,
                                max: prod.quantity,
                                style: { textAlign: "center" },
                              }}
                              value={prod.userQty}
                              onChange={(e) => {
                                const qty = Number(e.target.value);
                                const updated = selectedProducts.map((p) =>
                                  p.id === prod.id ? { ...p, userQty: qty } : p
                                );
                                setSelectedProducts(updated);

                                const total = updated.reduce(
                                  (sum, p) => sum + p.price * p.userQty,
                                  0
                                );
                                setValue("amount", total);
                              }}
                              sx={{ width: 90 }}
                            />
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </GridItem>
                )}
                <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
                  <ControlledTextInput
                    name="amount"
                    control={control}
                    labelText="Amount *"
                    disabled
                    formControlProps={{
                      fullWidth: true,
                      type: "number",
                    }}
                  />
                </GridItem>
              </GridContainer>
            </form>
          </Box>
        </DialogContent>
        <DialogActions>
          <MDButton
            type="submit"
            color="primary"
            variant="gradient"
            id="submit"
            onClick={handleSubmit(onSubmit)}
          >
            Sumbit
          </MDButton>
          <MDButton color="secondary" variant="gradient" autoFocus onClick={handleClose}>
            Cancel
          </MDButton>
        </DialogActions>
      </BootstrapDialog>
    </>
  );
};

EditCreditNoteDialog.propTypes = {
  // children: PropTypes.node.isRequired, // You can also use PropTypes.element if you want to enforce a single element.
  creditData: PropTypes.object.isRequired,
  companiesList: PropTypes.array.isRequired,
};

export default EditCreditNoteDialog;
